import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from "react-router-dom";
import { Button } from 'react-bootstrap';
import Hls from 'hls.js';
import api from '../../services/api';
import { toast } from 'react-toastify';

// When the stream carries no PDT, we anchor a press on the AM backend's live edge (ingest_start +
// captured duration). The operator hears audio slightly behind that edge (player + CDN buffer), so we
// nudge the anchor back by this estimate. The server's retroactive window absorbs the residual error.
const LIVE_EDGE_DELAY_SECONDS = 6;
const COOLDOWN_MS = 2000;

// Pick a readable text colour (dark/light) for a given hex background via perceived luminance, so a
// campaign colour of any brightness keeps its button label legible.
const readableText = (hex) => {
    if (!hex) return undefined;
    const c = hex.replace('#', '');
    const full = c.length === 3 ? c.split('').map((x) => x + x).join('') : c;
    if (full.length !== 6) return undefined;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.6 ? '#1b1b1b' : '#ffffff';
};

const MomentConsole = () => {
    const [streams, setStreams] = useState([]);
    const [streamId, setStreamId] = useState(0);
    const [stream, setStream] = useState(null);
    const [pdtStatus, setPdtStatus] = useState('');
    const [triggerTokens, setTriggerTokens] = useState([]);
    const [buttonState, setButtonState] = useState({}); // trigger-token id -> 'idle' | 'firing' | 'cooldown'
    const [fires, setFires] = useState([]); // session log, newest first

    const videoRef = useRef(null);
    const hlsRef = useRef(null);

    const destroyPlayer = () => {
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }
    };

    const attachPlayer = (url, isHls) => {
        destroyPlayer();
        const video = videoRef.current;
        if (!video || !url || !isHls) return;
        if (Hls.isSupported()) {
            const hls = new Hls({ lowLatencyMode: true, backBufferLength: 30 });
            hls.loadSource(url);
            hls.attachMedia(video);
            hlsRef.current = hls;
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = url; // Safari plays HLS natively
        }
    };

    const getStreams = () => {
        api.get('amstreams?limit=1000')
            .then((r) => { if (r.status == 200) setStreams(r.data.results); })
            .catch(() => {});
    };

    const getTriggerTokens = () => {
        api.get('momenttrigger/trigger-tokens')
            .then((r) => { if (r.status == 200) setTriggerTokens(r.data); })
            .catch(() => setTriggerTokens([]));
    };

    const getStatus = (id) => {
        setPdtStatus('');
        api.get(`amstream/${id}/status`)
            .then((r) => { if (r.status == 200) setPdtStatus(r.data.pdtStatus || ''); })
            .catch(() => {});
    };

    const onSelectStream = (id) => {
        const numId = parseInt(id, 10);
        setStreamId(numId);
        const s = streams.find((x) => x.id === numId) || null;
        setStream(s);
        setPdtStatus('');
        if (s) getStatus(s.id);
        // Player attach/detach happens in the effect below, once the <video> is mounted.
    };

    // The content-time to fire against, resolved at the instant of the press:
    //  - PDT present: the player's absolute program time — exactly what the operator is hearing.
    //  - otherwise: the AM backend's live edge (ingest_start + captured duration) minus the buffer delay.
    const computeAnchor = async () => {
        const playingDate = hlsRef.current ? hlsRef.current.playingDate : null;
        if (playingDate) return new Date(playingDate.getTime());
        try {
            const r = await api.get(`amstream/${stream.id}/status`);
            const d = r.data || {};
            if (d.ingestStartUtc && typeof d.capturedSeconds === 'number') {
                const edgeMs = new Date(d.ingestStartUtc).getTime() + d.capturedSeconds * 1000;
                return new Date(edgeMs - LIVE_EDGE_DELAY_SECONDS * 1000);
            }
        } catch (e) { /* fall through to wall-clock */ }
        return new Date(); // last resort: real-time ingest ⇒ content-time ≈ now
    };

    const fireToken = async (t) => {
        const state = buttonState[t.id] || 'idle';
        if (state === 'firing' || state === 'cooldown') return;
        setButtonState((s) => ({ ...s, [t.id]: 'firing' }));

        const anchor = await computeAnchor();
        const contentTime = anchor.toISOString();
        api.post('momenttrigger/live-fire', {
            token: stream.token, trigger_token: t.id, content_time: contentTime, label: t.name,
        })
            .then((r) => {
                if (r.status == 200) {
                    const n = typeof r.data.registered === 'number' ? r.data.registered : null;
                    if (n === 0) {
                        toast.warning(
                            `Fired "${t.name}" but captured 0 audio chunks — the anchor is outside the ingested audio, so it won't trigger.`,
                            { position: toast.POSITION.TOP_RIGHT });
                    } else {
                        toast.success(
                            `Fired: ${t.name}${n != null ? ` (${n} chunk${n === 1 ? '' : 's'})` : ''}`,
                            { position: toast.POSITION.TOP_RIGHT });
                    }
                    setFires((f) => [
                        { momentId: r.data.id, label: t.name, contentTime, registered: n, undone: false },
                        ...f,
                    ]);
                    setButtonState((s) => ({ ...s, [t.id]: 'cooldown' }));
                    setTimeout(() => setButtonState((s) => ({ ...s, [t.id]: 'idle' })), COOLDOWN_MS);
                }
            })
            .catch((error) => {
                const detail = error?.response?.data;
                const message = detail && typeof detail === 'object'
                    ? Object.values(detail).flat().join(' ')
                    : 'Fire failed';
                toast.error(message, { position: toast.POSITION.TOP_RIGHT });
                setButtonState((s) => ({ ...s, [t.id]: 'idle' }));
            });
    };

    // Undo a mis-press: DELETE cancels the durable fingerprints on the AM backend and removes the row.
    const undoFire = (fire) => {
        api.delete(`momenttrigger/${fire.momentId}`)
            .then(() => {
                setFires((f) => f.map((x) => (x.momentId === fire.momentId ? { ...x, undone: true } : x)));
                toast.info(`Undone: ${fire.label}`, { position: toast.POSITION.TOP_RIGHT });
            })
            .catch(() => toast.error('Undo failed', { position: toast.POSITION.TOP_RIGHT }));
    };

    useEffect(() => {
        getStreams();
        getTriggerTokens();
        return () => destroyPlayer();
    }, []);

    // Attach the HLS preview only after the <video> element is actually mounted. The <video> is
    // rendered conditionally on `stream`, so attaching synchronously inside onSelectStream races the
    // render and finds a null ref on the first selection — hence the blank preview. Keying off
    // `stream` here guarantees the element exists before we attach.
    useEffect(() => {
        if (stream && stream.stream_type === 'Hls') {
            attachPlayer(stream.url, true);
        } else {
            destroyPlayer();
        }
    }, [stream]);

    const pdtBadge = () => {
        const map = {
            yes: ['badge-success', 'PDT: yes (exact anchor)'],
            no: ['badge-warning', 'PDT: no (live-edge anchor)'],
            pending: ['badge-secondary', 'PDT: pending'],
            not_applicable: ['badge-secondary', 'PDT: N/A (SRT)'],
        };
        const [cls, text] = map[pdtStatus] || ['badge-secondary', 'PDT: unknown'];
        return <span className={`badge ${cls}`} style={{ marginLeft: 8 }}>{text}</span>;
    };

    const isHls = stream && stream.stream_type === 'Hls';

    return (
        <div className="card">
            <div className="card-body">
                <div className="row">
                    <div className="mb-3 col-md-6 col-sm-12">
                        <label className="form-label">Live stream</label>
                        <select className="form-control" value={streamId} onChange={(e) => onSelectStream(e.target.value)}>
                            <option value={0}>Select a stream…</option>
                            {streams.map((s) => (
                                <option key={s.id} value={s.id}>{s.token_name} — {s.stream_type}</option>
                            ))}
                        </select>
                    </div>
                    {stream ? (
                        <div className="mb-3 col-md-6 col-sm-12 d-flex align-items-end">
                            <div>{pdtBadge()}</div>
                        </div>
                    ) : null}
                </div>

                {stream ? (
                    <div className="row">
                        <div className="col-md-7 col-sm-12 mb-3">
                            {isHls ? (
                                <video
                                    ref={videoRef}
                                    controls
                                    autoPlay
                                    muted
                                    style={{ width: '100%', background: '#000', borderRadius: 6 }}
                                />
                            ) : (
                                <div className="alert alert-info">
                                    This stream is ingested via SRT and cannot be previewed in the browser.
                                    Presses anchor on the AM backend's live edge.
                                </div>
                            )}
                            <small className="form-text text-muted">
                                Tap a trigger the instant you hear the moment in <strong>this</strong> feed. It fires at
                                the current content position; the window leans slightly retroactive to catch your reaction
                                lag. Missed one? Use <strong>Add historical trigger</strong> below.
                            </small>

                            <h5 className="mt-3">Fires this session</h5>
                            {fires.length === 0 ? (
                                <p className="text-muted">No triggers fired yet.</p>
                            ) : (
                                <ul className="list-unstyled">
                                    {fires.map((fire) => (
                                        <li key={fire.momentId} className="mb-1 d-flex align-items-center">
                                            <span className="text-muted" style={{ width: 90 }}>
                                                {new Date(fire.contentTime).toLocaleTimeString()}
                                            </span>
                                            <strong style={{ minWidth: 120 }}>{fire.label}</strong>
                                            <span className={fire.registered === 0 ? 'text-warning' : 'text-muted'} style={{ minWidth: 90 }}>
                                                {fire.registered != null ? `${fire.registered} chunk${fire.registered === 1 ? '' : 's'}` : ''}
                                            </span>
                                            {fire.undone ? (
                                                <span className="text-muted"><em>undone</em></span>
                                            ) : (
                                                <Button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => undoFire(fire)}
                                                >
                                                    Undo
                                                </Button>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="col-md-5 col-sm-12">
                            <h5>Triggers</h5>
                            {triggerTokens.length === 0 ? (
                                <p className="text-muted">
                                    No eligible trigger tokens — a token needs active scheduled content to be fired here.
                                </p>
                            ) : (
                                triggerTokens.map((t) => {
                                    const state = buttonState[t.id] || 'idle';
                                    // Match the button to the delivered campaign's colour when set; otherwise
                                    // fall back to the theme's primary button.
                                    const style = { width: '100%', padding: '18px', fontSize: '1.2rem' };
                                    if (t.color) {
                                        style.backgroundColor = t.color;
                                        style.borderColor = t.color;
                                        const txt = readableText(t.color);
                                        if (txt) style.color = txt;
                                    }
                                    return (
                                        <Button
                                            key={t.id}
                                            className="btn btn-primary btn-lg btn-block mb-2"
                                            disabled={state !== 'idle'}
                                            onClick={() => fireToken(t)}
                                            style={style}
                                        >
                                            {state === 'firing' ? 'Firing…'
                                                : state === 'cooldown' ? 'Fired ✓'
                                                    : t.name}
                                        </Button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
            <div className="card-footer">
                <NavLink className="btn btn-secondary mr-3" to="/manage/momenttrigger/form">
                    <i className="fa fa-fw fa-clock-o"></i> Add historical trigger
                </NavLink>
                <NavLink className="btn btn-secondary" to="/manage/momenttriggers">
                    <i className="fa fa-fw fa-list"></i> Manage moment triggers
                </NavLink>
            </div>
        </div>
    );
};

export default MomentConsole;
